import React, { useEffect, useReducer, useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonRow,
  IonText,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { addUpdate } from '../utilities/Updates';
import { getJob, getSubmittedExpenses, storeSubmittedExpenses, updateStoredObjectKey } from '../utilities/Storage';
import { userIsSelfEmployed } from '../dataStructures/user/predicates';
import { Toolbar } from './Toolbar';
import { useAuthentication } from '../authentication/authentication-hook';

export default function JobExpenses({history, match}) {

  const id = match.params.id;

  const [job, setJob] = useState();
  const [errorToast, setErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const { user } = useAuthentication();
  const isNumeric = (value) => isFinite(value) && null !== value;

  const reducer = (state, action) => {
    switch (action.type) {
      case 'AMEND_EXPENSE':
        let ae = {...state.existingExpenses};
        ae[action.id] = {...ae[action.id], ...action.payload};
        return {...state, existingExpenses: ae};
      case 'ADD_NEW_EXPENSE': {
        let ne = [...state.newExpenses];
        ne.push(action.payload);
        return {...state, newExpenses: ne};
      }
      case 'AMEND_NEW_EXPENSE': {
        let ne = [...state.newExpenses];
        ne[action.index] = action.payload;
        return {...state, newExpenses: ne};
      }
      case 'REMOVE_NEW_EXPENSE': {
        let ne = [...state.newExpenses];
        ne.splice(action.index, 1);
        return {...state, newExpenses: ne};
      }
      case 'SET_ALERT':
        return {...state, alert: action.id};
      case 'SET_NEW_ALERT':
        return {...state, newAlert: action.id};  
      case 'TOGGLE_ADD_ALERT':
        return {...state, addAlert: !state.addAlert};
      case 'SET_DISABLE_ALL':
        return {...state, disableAll: action.payload};
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    existingExpenses: {},
    newExpenses: [],
    alert: 0,
    newAlert: -1,
    addAlert: false,
    disableAll: false,
  });

  useEffect(() => {
    const getData = () => {
      getJob(id).then(job => {
        setJob(job);

        let statusList = ['new', 'complete', 'started'];
        if (userIsSelfEmployed(user)) {
          statusList.push('completed');
        }

        if (!statusList.includes(job.status.toLowerCase())) {
          // job is financially complete so expenses can't be modified in any wayyyyyyyyyyyyyyyyy
          dispatch({type: 'SET_DISABLE_ALL', payload: true});
        }

        for (const expense of job.expenses) {
            dispatch({type: 'AMEND_EXPENSE', id: expense.id, payload: expense});
        }
      });

      getSubmittedExpenses(id).then(se => {
        if (!se.existing) {
          return;
        }

        for (const expenseId in se.existing) {
          const expense = se.existing[expenseId];
          dispatch({type: 'AMEND_EXPENSE', id: expense.id, payload: {
            amountnet: expense.amountnet,
            notes: expense.notes,
          }});
        }

        for (const expense of se.new) {
          const find = Object.values(se.existing).find(existing => {
            return existing.amountnet == expense.amountnet &&
              existing.description === expense.description; 
          });

          if (find) {
            // previously added expense is now actually on the job, so don't
            // show it as a new one now otherwise it'll be listed twice
            continue;
          }

          dispatch({type: 'ADD_NEW_EXPENSE', payload: {
            amountnet: expense.amountnet,
            description: expense.description,
          }});
        }
      });
    }

    getData();
  }, []);

  const save = async () => {

    let allJobExpenses = [];
    for (const expenseId in state.existingExpenses) {
      const expense = state.existingExpenses[expenseId];
      allJobExpenses.push(expense);

      if (!expense.updated) {
        continue;
      }
      let obj = {
        amountnet: expense.amountnet,
        notes: expense.notes,
      };
      if (userIsSelfEmployed(user)) {
        obj.id = expense.id;
        obj.description = expense.description;
        obj.currencyid = expense.currency.id;
        obj.vatbandid = expense.vatband.id;
        obj.ownerchargeamount = expense.ownerchargeamount;
        obj.donotpaysupplier = expense.donotpaysupplier;
        obj.invoiceto = expense.invoiceto;
        obj.costitemcodeid = expense.costitemcode.id;
        obj.amountnetestimate = expense.amountnetestimate;
        obj.amountvat = expense.amountvat;
        obj.chargingperiodsactual = '';
        obj.amountIncreased = !!expense.amountIncreased;
      }

      await addUpdate('update_expense', {
        jobId: job.id, 
        expenseId, 
        obj: obj,
      });
    }

    for (let expense of state.newExpenses) {
      allJobExpenses.push(expense);
      if (!expense.updated) {
        continue;
      }

      await addUpdate('add_new_expense', {
        jobId: job.id,
        obj: {
          amountnet: expense.amountnet,
          description: expense.description,
          currencyid: 1,
        },
      });
    }

    job.expenses = allJobExpenses;
    await updateStoredObjectKey('jobs', job.id, job);

    await storeSubmittedExpenses(
      job.id, 
      {
        existing: state.existingExpenses, 
        new: state.newExpenses
      }
    );

    if (job.substatus.workordersubstatus?.substatusreference === 'field_worker_to_provide_estimates') {
      await addUpdate('job_estimates_provided', {
        jobId: job.id,
      });
    }

    window.dispatchEvent(new Event("refreshList"));
    history.goBack();
  }

  const showError = error => {
    setErrorToastMessage(error);
    setErrorToast(true);
  }

  const pageTitle = `${id} expenses`;
  return (
    <IonPage>
      <IonHeader>
        <Toolbar title={pageTitle} backButtonHref="/" />
        <IonToolbar color="light" className="ion-padding-start ion-padding-end">
          {!state.disableAll ?
          <p>Note that stock items may not be modified.</p> :
          <p>Expenses may not be modified at this point.</p>
          }
        </IonToolbar>          
      </IonHeader>
      <IonContent>
        <IonList lines="full">
          <IonListHeader>
            Current expenses
          </IonListHeader>
          {Object.keys(state.existingExpenses).length === 0 &&
          <IonItem key="nolist">
            <IonLabel>
              None found.
            </IonLabel>
          </IonItem>
          }
          {Object.values(state.existingExpenses).map(expense => {
            if (expense.agencycostnet) {
              return (
                <IonItem key={expense.id}>
                  <IonLabel>
                    {expense.costitemcode?.externaldescription}
                    <p>{expense.description}</p>
                  </IonLabel>
                  <IonText color="dark">
                    <span>STOCK ITEM</span>
                  </IonText>
                </IonItem>
              );
            } else {
              return (
                <IonItem 
                  key={expense.id} 
                  onClick={() => { dispatch({type: 'SET_ALERT', id: expense.id}) }} 
                  button 
                  detail
                  disabled={state.disableAll}
                >
                  <IonLabel>
                    {expense.costitemcode?.externaldescription}
                    <p>{expense.description}</p>
                  </IonLabel>

                  {isNumeric(expense.amountnet) && parseFloat(expense.amountnet) !== 0 &&
                      <IonText>
                        £{parseFloat(expense.amountnet).toFixed(2)} NET
                      </IonText>
                  }

                  {expense.amountnetestimate && expense.amountnet !== expense.amountnetestimate && (
                      <IonText
                          style={{
                            textDecoration: expense.amountnet ? 'line-through' : 'none'
                          }}
                      >
                        £{parseFloat(expense.amountnetestimate).toFixed(2)} NET
                      </IonText>
                  )}
                  <IonAlert
                    isOpen={state.alert === expense.id}
                    onDidDismiss={() => dispatch({type: 'SET_ALERT', id: 0}) }
                    header={'Update Estimate'}
                    message={'Enter the NET amount below (VAT will be added if applicable). '}
                    inputs={[
                      {
                        name: 'amountnet',
                        type: 'number',
                        placeholder: '0',
                        value: parseFloat(expense.amountnet || expense.amountnetestimate).toFixed(2)
                      },
                      {
                        name: 'notes',
                        type: 'text',
                        placeholder: 'reason for change',
                        value: expense.notes,
                      },
                    ]}
                    buttons={[
                      {
                        text: 'Cancel',
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                          dispatch({type: 'SET_ALERT', id: 0});
                        }
                      },
                      {
                        text: 'Save',
                        handler: data => {
                          const newAmount = parseFloat(data.amountnet);

                          if (!data.amountnet) {
                            showError('Please enter an updated amount.');
                            return false;
                          }

                          if (userIsSelfEmployed(user)) {
                            if (newAmount > expense.amountnetestimate && !data.notes) {
                              showError('Please enter a reason for increasing the amount.');
                              return false;
                            }
                          } else {
                            if (!data.notes) {
                              showError('Please enter a reason for change.');
                              return false;
                            }
                          }
      
                          dispatch({
                            type: 'AMEND_EXPENSE', 
                            id: expense.id, 
                            payload: {
                              amountnet: parseFloat(data['amountnet']), 
                              notes: data.notes,
                              updated: true,
                              amountIncreased: newAmount > expense.amountnetestimate
                            }
                          });
                        }
                      }
                    ]}
                  />
                </IonItem>   
              );
            }
          })}
        </IonList>
        <IonList lines="full">
          <IonListHeader>
            New expenses
          </IonListHeader>
          {state.newExpenses.length === 0 &&
          <IonItem key="nolist">
            <IonLabel>
              No new expenses yet.
            </IonLabel>
          </IonItem>
          }
          {state.newExpenses.map((expense, i) =>
          <IonItem 
            key={i} 
            onClick={() => { dispatch({type: 'SET_NEW_ALERT', id: i}) }} 
            button 
            detail
            disabled={state.disableAll}
          >
            <IonLabel>
              {expense.description}
            </IonLabel>
            <IonText color="dark" style={{textDecoration: "underline"}}>
              £{parseFloat(expense.amountnet).toFixed(2)}
            </IonText>
            <IonAlert
              isOpen={state.newAlert === i}
              onDidDismiss={() => dispatch({type: 'SET_NEW_ALERT', id: -1}) }
              header="update item"
              message="Enter the NET amount below (VAT will be added if applicable)."
              inputs={[
                {
                  name: 'description',
                  type: 'text',
                  placeholder: 'description',
                  value: expense.description
                },
                {
                  name: 'amountnet',
                  type: 'number',
                  placeholder: '0',
                  value: expense.amountnet
                }
              ]}
              buttons={[
                {
                  text: 'Cancel',
                  role: 'cancel',
                  cssClass: 'secondary',
                  handler: () => {
                    dispatch({type: 'SET_NEW_ALERT', id: -1});
                  }
                },
                {
                  text: 'Save',
                  handler: data => {
                    // if (!data.amountnet) {
                    //   showError('Please enter an amount.');
                    //   return false;
                    // }
                    if (!data.description) {
                      showError('Please enter a description.');
                      return false;
                    }

                    dispatch({
                      type: 'AMEND_NEW_EXPENSE', 
                      index: i,
                      payload: { 
                        amountnet: parseFloat(data['amountnet'] || 0), 
                        description: data.description,
                        updated: true,
                      }
                    });                    
                  }
                }
              ]}
            />
          </IonItem>            
          )}
        </IonList>
      </IonContent>
      <IonFooter no-border>
        <IonToolbar className="ion-text-center">
          <IonGrid>
            <IonRow>
              {!userIsSelfEmployed(user) && (
                  <IonCol>
                    <IonButton
                        color="medium"
                        expand="block"
                        onClick={() => { dispatch({type: 'TOGGLE_ADD_ALERT'}) }}
                        disabled={state.disableAll}
                    >
                      Add Expense
                    </IonButton>
                  </IonCol>
              )}
              <IonCol>
                <IonButton 
                  color="medium"
                  expand="block" 
                  onClick={save}
                  disabled={state.disableAll}
                >
                  Save Changes
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonToolbar>

        <IonAlert
          isOpen={state.addAlert}
          onDidDismiss={() => dispatch({type: 'TOGGLE_ADD_ALERT'}) }
          header={'add expense'}
          message={'Enter a description and amount below.'}
          inputs={[
            {
              name: 'description',
              type: 'text',
              placeholder: 'description'
            },              
            {
              name: 'amountnet',
              type: 'number',
              placeholder: '0'
            }
          ]}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              cssClass: 'secondary',
            },
            {
              text: 'Save',
              handler: data => {
                // if (!data.amountnet) {
                //   showError('Please enter an amount.');
                //   return false;
                // }
                if (!data.description) {
                  showError('Please enter a description.');
                  return false;
                }

                dispatch({type: 'ADD_NEW_EXPENSE', payload: {
                  amountnet: parseFloat(data['amountnet'] || 0), 
                  description: data.description,
                  updated: true,
                }});
              }
            }
          ]}
        />

      </IonFooter>
      <IonToast
        isOpen={errorToast}
        onDidDismiss={() => setErrorToast(false) }
        message={errorToastMessage}
        duration={2000}
        color="danger"
        position="middle"
      />
    </IonPage>
  );
}
